import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { ChevronUp } from "lucide-react"
import { useState } from "react"
import type { WorklistIssue } from "../../studio/cellState"
import type { Verdict } from "../../studio/studioTypes"
import { openWorkByFramework } from "../mapping/frameworkGeprueft"
import { SupplierAssignControl } from "../supplier/SupplierAssignControl"
import { issueReasonKey } from "./issueReason"
import { issuesInDisplayOrder } from "./nextOpenPoint"

export interface OpenPointsPopoverProps {
  declId: string
  worklist: WorklistIssue[]
  nameOf: (framework: string) => string
  /** `null` = not yet known — only a confirmed `false` counts as a blocker,
   *  mirroring the approve button's own gate. */
  supplierMatched: boolean | null
  supplierMentioned: string | null
  /** Frameworks with a Rückfrage out (`inquiryStateOf(fw) === "sent"`) —
   *  passed pre-filtered so this file needs no import from the footer (the
   *  footer imports THIS file). */
  sentFrameworks: string[]
  /** `step` (W5-4): each row is ONE issue, so it always carries its own
   *  step, the jump names it, and the landed card opens that step's own
   *  dialog too (`UnitCard`'s own `focusStep`). */
  onGoToStatement: (
    framework: string,
    statementId: string,
    step?: 1 | 2 | 3,
  ) => void
  onGoToFramework: (framework: string) => void
  /** The "Aussage N" number for a statement, by its DISPLAYED card position —
   *  not the raw `groupIndex` (the staged row's merge-order ordinal). Since
   *  TUR-1211 (W2-3) the unit cards sort by document (evidence-page) order,
   *  so a bare `groupIndex + 1` can name a different statement than the one
   *  the reviewer sees at that position on the card list. Optional, falling
   *  back to `groupIndex + 1` (the pre-sort behaviour) for callers/tests that
   *  predate this — `StudioV2Layout` is the only real caller and always
   *  passes it, built from the SAME `statementCardsByFramework` the cards
   *  render from, so the two labels can never disagree. */
  labelOf?: (
    framework: string,
    statementId: string,
    groupIndex: number | undefined,
  ) => number
  /** Statement ids in DISPLAY order per framework — the same function
   *  `nextOpenPoint` receives, so the rows of a section list in the order
   *  Weiter walks and `labelOf` numbers them (1, 2, 3 top-to-bottom, never
   *  3, 1, 2). Optional with the same no-ids default as `nextOpenPoint`. */
  cardOrderOf?: (framework: string) => readonly string[]
  /** A statement's own verdict, which words its step-3 row (`issueReasonKey`,
   *  TUR-1676: a card without a substances section has no Confirm there).
   *  Optional, resolving nothing by default: every caller/test predating it
   *  keeps the substances wording. */
  verdictOf?: (statementId: string) => Verdict | null | undefined
  /** A statement row's reason text, naming its exact blocker ("Beryllium:
   *  Endpunkt offen", "Stoffangaben bestätigen · 2 von 103 Artikeln").
   *  Optional, defaulting to the plain step reason (`issueReasonKey`). */
  reasonOf?: (issue: WorklistIssue, statementId: string) => string
  /** Framework slugs in navigator order: sections list in this order, so the
   *  map reads top to bottom like the sidebar. */
  frameworkOrder?: readonly string[]
  /** STEP C3-b3: called when the map opens. The paged review loads the other
   *  regulations' cards then, so every row's reason reads as it does flag off. */
  onOpen?: () => void
}

/** Everything that currently blocks Freigeben, counted the way the approve
 *  gate itself counts: open worklist issues, an unmatched supplier, and any
 *  Rückfrage still out. Exported for the footer's "show counter vs. all
 *  checked" branch so the two can never disagree. */
export function countOpenPoints({
  worklist,
  supplierMatched,
  sentFrameworks,
}: Pick<
  OpenPointsPopoverProps,
  "worklist" | "supplierMatched" | "sentFrameworks"
>): number {
  return (
    worklist.length +
    (supplierMatched === false ? 1 : 0) +
    sentFrameworks.length
  )
}

/** The pre-TUR-1211-reorder "Aussage N" numbering: plain `groupIndex + 1`.
 *  Exported so the footer's blocked-Approve tooltip (TUR-1563) can name a
 *  statement the SAME way this popover does, without a second definition
 *  drifting from this one. */
export const defaultLabelOf: NonNullable<OpenPointsPopoverProps["labelOf"]> = (
  _framework,
  _statementId,
  groupIndex,
) => (groupIndex ?? 0) + 1

/** No known display order: every existing caller/test predating
 *  `cardOrderOf`. Exported for the same reason as `defaultLabelOf`. */
export const defaultCardOrderOf: NonNullable<
  OpenPointsPopoverProps["cardOrderOf"]
> = () => []

/** No known verdict: every existing caller/test predating `verdictOf`.
 *  Exported for the same reason as `defaultLabelOf`. */
export const defaultVerdictOf: NonNullable<
  OpenPointsPopoverProps["verdictOf"]
> = () => undefined

/** Frameworks in navigator order, unknown ones after in their given order. */
export function inFrameworkOrder(
  frameworks: readonly string[],
  order: readonly string[],
): string[] {
  const rank = (fw: string) => {
    const i = order.indexOf(fw)
    return i === -1 ? order.length : i
  }
  return [...frameworks].sort((a, b) => rank(a) - rank(b))
}

/**
 * D1, the map (TUR-1449): the live "Noch n offene Punkte" counter in the
 * footer's gate-text slot, with a popover itemising every Freigeben blocker
 * BEFORE the reviewer runs into the post-click approve gate. Same lines the
 * gate shows, each one a jump: statement rows switch the mask to that
 * statement, framework rows (reference-list conflict, open Rückfrage) switch
 * to that Verordnung, the supplier row mounts the same assign/create control
 * the gate's NO_SUPPLIER branch uses. Computed client-side from the worklist
 * that already drives the sidebar dots and the approve gate — never a new
 * query. The post-click gate stays as fallback (server-known blockers like
 * MISSING_LIST_VERSION can still surface there).
 */
export function OpenPointsPopover({
  declId,
  worklist,
  nameOf,
  supplierMatched,
  supplierMentioned,
  sentFrameworks,
  onGoToStatement,
  onGoToFramework,
  labelOf = defaultLabelOf,
  cardOrderOf = defaultCardOrderOf,
  verdictOf = defaultVerdictOf,
  reasonOf,
  frameworkOrder = [],
  onOpen,
}: OpenPointsPopoverProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)

  const count = countOpenPoints({ worklist, supplierMatched, sentFrameworks })
  if (count === 0) return null

  const byFramework = openWorkByFramework(worklist)
  // A framework can owe a Rückfrage without any worklist issue left — union
  // both sources so its section still renders, in worklist-first order.
  const sectionFrameworks = inFrameworkOrder(
    [
      ...byFramework.keys(),
      ...sentFrameworks.filter((fw) => !byFramework.has(fw)),
    ],
    frameworkOrder,
  )

  const jumpToStatement = (
    framework: string,
    statementId: string,
    step: 1 | 2 | 3,
  ) => {
    setOpen(false)
    onGoToStatement(framework, statementId, step)
  }
  const jumpToFramework = (framework: string) => {
    setOpen(false)
    onGoToFramework(framework)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) onOpen?.()
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="sv2-openpts"
          data-testid="open-points-trigger"
        >
          {translate("MC_DECL_V2_OPEN_POINTS_COUNT", { n: count })}
          <ChevronUp size={13} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={10}
        // Same fix HelpPopover documents: Radix portals to `document.body`,
        // OUTSIDE `.sv2-root`'s CSS-variable scope — every sv2 token in
        // `.sv2-openpop` would resolve empty (transparent card, wrong
        // border). `disablePortal` keeps the node inside the scope.
        disablePortal
        // Escape must CLOSE THE MAP, not leave the workspace: Radix's own
        // Escape listener runs document-capture, before `useStudioV2Keyboard`'s
        // window bubble listener (Esc-to-list) — stopping propagation here
        // swallows the keypress for that global handler while Radix's own
        // dismiss still fires. Same documented pattern as HelpPopover
        // (Copilot, this PR).
        onEscapeKeyDown={(e) => e.stopPropagation()}
        className="w-[360px] rounded-none border-0 bg-transparent p-0 shadow-none"
      >
        <div className="sv2-openpop" data-testid="open-points-popover">
          <p className="sv2-opt">{translate("MC_DECL_V2_OPEN_POINTS_TITLE")}</p>
          {supplierMatched === false && (
            <div className="sv2-opsec">
              <p className="sv2-oprow-static">
                {translate("MC_DECL_V2_OPEN_POINTS_SUPPLIER")}
              </p>
              <SupplierAssignControl
                declId={declId}
                supplierMentioned={supplierMentioned}
                trigger={
                  <button type="button" className="sv2-btn sv2-btn-sm">
                    {translate("MC_DECL_V2_SUP_ASSIGN_CTA")}
                  </button>
                }
              />
            </div>
          )}
          {sectionFrameworks.map((fw) => (
            <div key={fw} className="sv2-opsec">
              <p className="sv2-opfw">{nameOf(fw)}</p>
              {issuesInDisplayOrder(
                byFramework.get(fw) ?? [],
                cardOrderOf(fw),
              ).map((issue) =>
                issue.unit === "statement" && issue.statementId ? (
                  <StatementRow
                    key={`${issue.unitId}:${issue.step}`}
                    label={translate("MC_DECL_V2_UNIT_LABEL", {
                      i: labelOf(fw, issue.statementId, issue.groupIndex),
                    })}
                    reason={
                      reasonOf
                        ? reasonOf(issue, issue.statementId)
                        : translate(
                            issueReasonKey(issue, verdictOf(issue.statementId))
                              .label,
                          )
                    }
                    hint={
                      issueReasonKey(issue, verdictOf(issue.statementId)).hint
                    }
                    onClick={() =>
                      jumpToStatement(fw, issue.statementId!, issue.step)
                    }
                  />
                ) : (
                  // The framework unit — the reference-list edition conflict
                  // (see `WorklistIssue`'s own doc): no statement to land on,
                  // so the jump switches to the Verordnung itself.
                  <button
                    key={`${issue.unitId}:${issue.step}`}
                    type="button"
                    className="sv2-oprow"
                    onClick={() => jumpToFramework(fw)}
                  >
                    {translate("MC_DECL_V2_OPEN_POINTS_REFLIST")}
                  </button>
                ),
              )}
              {sentFrameworks.includes(fw) && (
                <button
                  type="button"
                  className="sv2-oprow"
                  onClick={() => jumpToFramework(fw)}
                >
                  {translate("MC_DECL_V2_OPEN_POINTS_INQUIRY")}
                </button>
              )}
            </div>
          ))}
          {/* NOT `.sv2-gate`: inside the footer's gate slot that class would
              match `.sv2-revfoot .sv2-gate`'s right-aligned nowrap layout —
              `.sv2-opalt` restates the quiet-hint look on its own. */}
          <p className="sv2-opalt">
            {translate("MC_DECL_V2_GATE_ALTERNATIVES")}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * W5 step3-chip: one statement row, with the SAME hover explanation the
 * matching chip carries (`issueReasonKey`'s `hint`, null when no V3 gesture
 * clears that state, `IssueReason`'s own doc). Rendered with the popup's own
 * `disablePortal`-free Tooltip: its content is plain translated text, no
 * `sv2-*` custom-property classes, so it does not hit the CSS-variable-scope
 * hazard `disablePortal` on the surrounding `PopoverContent` exists for.
 * The tooltip's `bg-popover` is the same grey as the `.sv2-openpop` card it
 * overlaps, so it carries a 1px global-token border (TUR-1755): the sv2
 * `--line` would not resolve in the portal.
 */
function StatementRow({
  label,
  reason,
  hint,
  onClick,
}: {
  label: string
  reason: string
  hint: TranslationKeys | null
  onClick: () => void
}) {
  const { translate } = useTranslation()
  const button = (
    <button type="button" className="sv2-oprow" onClick={onClick}>
      <span className="sv2-opn">{label}</span>
      {reason}
    </button>
  )
  if (!hint) return button
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] border border-border"
        >
          {translate(hint)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
